import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useQuery } from "@apollo/client";
import deviceGroupService from "../../services/deviceGroupService";
import vppCatalogService from "../../services/vppCatalogService";
import macPackageService from "../../services/macPackageService";
import { getDdmStatus, getDeclarations } from "../../services/nanoDdmService";
import { GET_CONFIG_PROFILES } from "../../queries/configProfilesQueries";
import softwareUpdateService, {
  defaultDeadlineLocal,
  softwareUpdateVersionLabel,
  toTargetLocalDateTime,
} from "../../services/softwareUpdateService";
import SoftwareUpdateCatalogPicker from "../SoftwareUpdateCatalogPicker";
import DdmPredicateEditor from "../DdmPredicateEditor";

function packageUpdatePayload(pkg, { groups, udids }) {
  return {
    name: pkg.name,
    description: pkg.description || "",
    title: pkg.title || pkg.name,
    bundleIdentifier: pkg.bundleIdentifier,
    bundleVersion: pkg.bundleVersion,
    removeFilesOnUninstall: pkg.removeFilesOnUninstall !== false,
    installRequired: pkg.installRequired !== false,
    deliveryMode: pkg.deliveryMode || "auto",
    enabled: pkg.enabled !== false,
    groups,
    udids,
  };
}

function isAssignableDeclaration(decl) {
  const identifier = decl?.Identifier || decl?.identifier || "";
  const type = String(decl?.Type || decl?.type || "");
  if (!identifier || String(identifier).startsWith("xavier.")) return false;
  if (type.includes("softwareupdate.enforcement")) return false;
  return true;
}

function declarationLabel(decl) {
  const identifier = decl?.Identifier || decl?.identifier || "";
  const type = String(decl?.Type || decl?.type || "");
  const shortType = type.replace(/^com\.apple\.(configuration|activation|asset|management)\./, "");
  return { identifier, type, shortType };
}

export default function GroupAssignmentsModal({ visible, group, initialTab = "profiles", onClose, onSaved }) {
  const [tab, setTab] = useState(initialTab);
  const [saving, setSaving] = useState(false);
  const [profilesSel, setProfilesSel] = useState([]);
  const [declarationsSel, setDeclarationsSel] = useState([]);
  const [applications, setApplications] = useState([]);
  const [appConfigs, setAppConfigs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [catalogQuery, setCatalogQuery] = useState("");
  const [ddmAvailable, setDdmAvailable] = useState(false);
  const [declarations, setDeclarations] = useState([]);
  const [loadingExtras, setLoadingExtras] = useState(false);
  const [suIdentifier, setSuIdentifier] = useState(null);
  const [suDeadline, setSuDeadline] = useState(defaultDeadlineLocal());
  const [iosCatalog, setIosCatalog] = useState([]);
  const [macCatalog, setMacCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [activationPredicate, setActivationPredicate] = useState("");
  const [packages, setPackages] = useState([]);
  const [packagesSel, setPackagesSel] = useState([]);

  const { data: profilesData } = useQuery(GET_CONFIG_PROFILES, { skip: !visible });
  const profiles = profilesData?.configProfiles || [];

  useEffect(() => {
    if (!visible || !group) return;
    setTab(initialTab || "profiles");
    setProfilesSel((group.profiles || []).map((item) => String(item._id || item)));
    setDeclarationsSel((Array.isArray(group.declarations) ? group.declarations : []).filter((id) => !String(id).startsWith("xavier.")));
    setSuIdentifier(group.softwareUpdateIdentifier || null);
    setActivationPredicate(group.activationPredicate || "");
    setSuDeadline(defaultDeadlineLocal());
    setApplications(
      (group.applications || []).map((app) => ({
        adamId: app.adamId,
        bundleId: app.bundleId || "",
        name: app.name || "",
        pricingParam: app.pricingParam || "STDQ",
        productType: app.productType || "App",
        managedAppConfig: app.managedAppConfig?._id || app.managedAppConfig || "",
      }))
    );
    setCatalogQuery("");
    setPackages([]);
    setPackagesSel([]);
    setLoadingExtras(true);
    setCatalogLoading(true);
    setCatalogError("");
    const groupId = String(group._id);
    Promise.all([
      vppCatalogService.getAppConfigs().catch(() => []),
      vppCatalogService.getAssets().catch(() => ({ assets: [] })),
      getDdmStatus().catch(() => ({ available: false })),
      getDeclarations({ detail: true }).catch(() => ({ declarations: [], identifiers: [] })),
      macPackageService.listPackages().catch(() => []),
    ])
      .then(([configs, catalog, status, listed, pkgRows]) => {
        setAppConfigs(configs || []);
        setAssets(catalog.assets || []);
        setDdmAvailable(Boolean(status?.available));
        const rows = listed.declarations || [];
        setDeclarations(rows.filter(isAssignableDeclaration));
        const list = Array.isArray(pkgRows) ? pkgRows : [];
        setPackages(list);
        setPackagesSel(
          list
            .filter((pkg) => (pkg.groups || []).some((g) => String(g._id || g) === groupId))
            .map((pkg) => String(pkg._id)),
        );
      })
      .finally(() => setLoadingExtras(false));
    softwareUpdateService.getCatalogs()
      .then((catalogs) => {
        setIosCatalog(catalogs.ios || []);
        setMacCatalog(catalogs.macos || []);
      })
      .catch((err) => {
        setIosCatalog([]);
        setMacCatalog([]);
        setCatalogError(err.response?.data?.message || err.message || "Apple GDMF-Katalog konnte nicht geladen werden.");
      })
      .finally(() => setCatalogLoading(false));
  }, [visible, group, initialTab]);

  const assignedAdam = useMemo(
    () => new Set(applications.map((app) => String(app.adamId))),
    [applications]
  );

  const catalogVisible = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    return (assets || []).filter((app) => {
      if (assignedAdam.has(String(app.adamId))) return false;
      if (!q) return true;
      return [app.name, app.artistName, app.sellerName, app.bundleId, app.adamId]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [assets, assignedAdam, catalogQuery]);

  if (!visible || !group) return null;

  const toggleProfile = (id) => {
    setProfilesSel((prev) => {
      const next = [...prev];
      const idx = next.indexOf(id);
      if (idx >= 0) next.splice(idx, 1);
      else next.push(id);
      return next;
    });
  };

  const toggleDeclaration = (identifier) => {
    setDeclarationsSel((prev) => (
      prev.includes(identifier)
        ? prev.filter((item) => item !== identifier)
        : [...prev, identifier]
    ));
  };

  const togglePackage = (id) => {
    setPackagesSel((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const addApp = (app) => {
    setApplications((prev) => [
      ...prev,
      {
        adamId: app.adamId,
        bundleId: app.bundleId || "",
        name: app.name || app.adamId,
        pricingParam: app.pricingParam || "STDQ",
        productType: app.productType || "App",
        managedAppConfig: "",
      },
    ]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await deviceGroupService.updateDeviceGroup(group._id, {
        name: group.name,
        location: group.location?._id || group.location || undefined,
        profiles: profilesSel,
        declarations: declarationsSel,
        activationPredicate: activationPredicate || null,
        applications: applications.map((app) => ({
          ...app,
          managedAppConfig: app.managedAppConfig || null,
        })),
      });
      const currentSu = group.softwareUpdateIdentifier || null;
      if ((suIdentifier || null) !== currentSu) {
        if (suIdentifier) {
          const item = [...iosCatalog, ...macCatalog].find((row) => row.identifier === suIdentifier);
          await softwareUpdateService.setGroupSoftwareUpdate(group._id, {
            identifier: suIdentifier,
            family: item?.family,
            productVersion: item?.productVersion,
            targetLocalDateTime: toTargetLocalDateTime(suDeadline),
          });
        } else {
          await softwareUpdateService.setGroupSoftwareUpdate(group._id, { identifier: "none" });
        }
      }
      const groupId = String(group._id);
      const wanted = new Set(packagesSel);
      for (const pkg of packages) {
        const pkgId = String(pkg._id);
        const currentGroups = (pkg.groups || []).map((g) => String(g._id || g));
        const has = currentGroups.includes(groupId);
        const want = wanted.has(pkgId);
        if (has === want) continue;
        const nextGroups = want
          ? [...new Set([...currentGroups, groupId])]
          : currentGroups.filter((id) => id !== groupId);
        await macPackageService.updatePackage(
          pkgId,
          packageUpdatePayload(pkg, {
            groups: nextGroups,
            udids: pkg.udids || [],
          }),
        );
      }
      toast.success("Zuweisungen gespeichert. Geräte werden synchronisiert.");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Zuweisungen · {group.name}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <ul className="nav nav-tabs mb-3">
              {[
                ["profiles", `Profile (${profilesSel.length})`],
                ["os", `OS-Update${suIdentifier ? " · 1" : ""}`],
                ["packages", `Pakete (${packagesSel.length})`],
                ["ddm", `DDM (${declarationsSel.length})`],
                ["apps", `Apps (${applications.length})`],
              ].map(([id, label]) => (
                <li className="nav-item" key={id}>
                  <button
                    type="button"
                    className={`nav-link ${tab === id ? "active" : ""}`}
                    onClick={() => setTab(id)}
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>

            {tab === "profiles" && (
              <div>
                <p className="small text-muted">Reihenfolge der Auswahl ist die Installationsreihenfolge.</p>
                <div className="d-flex flex-column gap-1">
                  {profiles.map((profile) => (
                    <label key={profile._id} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={profilesSel.includes(String(profile._id))}
                        onChange={() => toggleProfile(String(profile._id))}
                      />
                      <span className="form-check-label">
                        {profile.PayloadDisplayName}
                        {profile.PayloadIdentifier ? (
                          <span className="small text-muted"> · {profile.PayloadIdentifier}</span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                  {profiles.length === 0 && <p className="text-muted mb-0">Keine Profile vorhanden.</p>}
                </div>
              </div>
            )}

            {tab === "os" && (
              <div>
                <p className="small text-muted">
                  Eine Zielversion für alle Gruppenmitglieder. Geräte können sie später überschreiben.
                  Versionen stammen aus Apple GDMF. Die gleiche Auswahl gibt es unter{" "}
                  <Link to="/software-updates">Software Updates</Link>.
                </p>
                <div className="mb-3" style={{ maxWidth: 280 }}>
                  <label className="form-label small">Deadline (lokal)</label>
                  <input
                    type="datetime-local"
                    className="form-control form-control-sm"
                    value={suDeadline}
                    onChange={(e) => setSuDeadline(e.target.value)}
                  />
                </div>
                <SoftwareUpdateCatalogPicker
                  iosCatalog={iosCatalog}
                  macCatalog={macCatalog}
                  value={suIdentifier}
                  onChange={setSuIdentifier}
                  loading={catalogLoading}
                  error={catalogError}
                  onRetry={() => {
                    setCatalogLoading(true);
                    setCatalogError("");
                    softwareUpdateService.getCatalogs({ refresh: true })
                      .then((catalogs) => {
                        setIosCatalog(catalogs.ios || []);
                        setMacCatalog(catalogs.macos || []);
                      })
                      .catch((err) => {
                        setIosCatalog([]);
                        setMacCatalog([]);
                        setCatalogError(err.response?.data?.message || err.message || "Apple GDMF-Katalog konnte nicht geladen werden.");
                      })
                      .finally(() => setCatalogLoading(false));
                  }}
                  name="group-su"
                />
                {suIdentifier ? (
                  <p className="small mb-0">Aktuelle Auswahl: {softwareUpdateVersionLabel(suIdentifier)}</p>
                ) : null}
              </div>
            )}

            {tab === "packages" && (
              <div>
                <p className="small text-muted">
                  Mac-.pkg aus <Link to="/mac-packages">Mac-Pakete</Link>. Zuweisung speichert die Gruppe am Paket und synct DDM / InstallEnterpriseApplication.
                </p>
                {loadingExtras ? <p className="text-muted">Lade Pakete…</p> : null}
                <div className="d-flex flex-column gap-1">
                  {packages.map((pkg) => {
                    const id = String(pkg._id);
                    return (
                      <label key={id} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={packagesSel.includes(id)}
                          onChange={() => togglePackage(id)}
                          disabled={!pkg.hasFile}
                        />
                        <span className="form-check-label">
                          {pkg.name}
                          {!pkg.hasFile ? (
                            <span className="small text-warning"> · Datei fehlt</span>
                          ) : (
                            <span className="small text-muted"> · {pkg.bundleIdentifier}</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                  {!loadingExtras && packages.length === 0 && (
                    <p className="text-muted mb-0">
                      Noch keine Pakete. Unter <Link to="/mac-packages">Mac-Pakete</Link> anlegen.
                    </p>
                  )}
                </div>
              </div>
            )}

            {tab === "ddm" && (
              <div>
                {!ddmAvailable ? (
                  <p className="text-muted mb-0">
                    NanoHUB DDM ist nicht konfiguriert. Deklarationen zuerst unter{" "}
                    <Link to="/ddm">DDM</Link> anlegen, sobald DDM erreichbar ist.
                  </p>
                ) : (
                  <>
                    <p className="small text-muted">
                      Gewählte Deklarationen liegen im Set dieser Gruppe. Geräte der Gruppe holen sie per DDM.
                      OS-Update-Enforcement liegt im Tab „OS-Update“, nicht in dieser Liste.
                      Anlegen unter <Link to="/ddm">DDM</Link> — inkl. Legacy-Profilen (WLAN, Single App, Layout) und Aktivierungen.
                    </p>
                    <div className="mb-3">
                      <div className="form-label">Aktivierung der Gruppen-Konfigurationen</div>
                      <DdmPredicateEditor value={activationPredicate} onChange={(next) => setActivationPredicate(next || "")} />
                    </div>
                    {loadingExtras ? <p className="text-muted">Lade Deklarationen…</p> : null}
                    <div className="d-flex flex-column gap-1">
                      {declarations.map((decl) => {
                        const meta = declarationLabel(decl);
                        return (
                          <label key={meta.identifier} className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={declarationsSel.includes(meta.identifier)}
                              onChange={() => toggleDeclaration(meta.identifier)}
                            />
                            <span className="form-check-label">
                              <code>{meta.identifier}</code>
                              {meta.shortType ? (
                                <span className="small text-muted"> · {meta.shortType}</span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })}
                      {!loadingExtras && declarations.length === 0 && (
                        <p className="text-muted mb-0">Noch keine benutzerdefinierten Deklarationen.</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === "apps" && (
              <div>
                <h6>Zugewiesen</h6>
                {applications.length === 0 ? (
                  <p className="small text-muted">Keine Apps in dieser Gruppe.</p>
                ) : (
                  <div className="d-flex flex-column gap-2 mb-3">
                    {applications.map((app) => (
                      <div key={app.adamId} className="d-flex flex-wrap gap-2 align-items-center">
                        <span className="small" style={{ minWidth: 180 }}>{app.name || app.adamId}</span>
                        <select
                          className="form-select form-select-sm"
                          style={{ maxWidth: 280 }}
                          value={app.managedAppConfig || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setApplications((prev) =>
                              prev.map((item) =>
                                item.adamId === app.adamId ? { ...item, managedAppConfig: value } : item
                              )
                            );
                          }}
                        >
                          <option value="">Keine Managed App Config</option>
                          {appConfigs
                            .filter((cfg) => String(cfg.adamId) === String(app.adamId))
                            .map((cfg) => (
                              <option key={cfg._id} value={cfg._id}>{cfg.name}</option>
                            ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setApplications((prev) => prev.filter((item) => item.adamId !== app.adamId))}
                        >
                          Entfernen
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <h6>Aus Katalog hinzufügen</h6>
                <input
                  className="form-control form-control-sm mb-2"
                  placeholder="App suchen…"
                  value={catalogQuery}
                  onChange={(e) => setCatalogQuery(e.target.value)}
                />
                <div style={{ maxHeight: 240, overflow: "auto" }}>
                  {catalogVisible.slice(0, 40).map((app) => (
                    <div key={app.adamId} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                      <div>
                        <div>{app.name || app.adamId}</div>
                        <div className="small text-muted">{app.artistName || app.sellerName || app.bundleId}</div>
                      </div>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addApp(app)}>
                        Hinzufügen
                      </button>
                    </div>
                  ))}
                  {catalogVisible.length === 0 && (
                    <p className="small text-muted mb-0">Keine weiteren Apps. Katalog unter <Link to="/applications">Applications</Link>.</p>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Abbrechen</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
              {saving ? "Speichere…" : "Zuweisungen speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
